import { useState, useEffect, useCallback } from 'react';

const API = '/api';

export default function AdminDashboard() {
  const [secret, setSecret] = useState(localStorage.getItem('admin_session') || '');
  const [totpCode, setTotpCode] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  const [setupSecret, setSetupSecret] = useState('');
  const [qrData, setQrData] = useState(null);
  const [activeTab, setActiveTab] = useState('shop');
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ name: '', description: '', image_url: '' });
  const [progressInputs, setProgressInputs] = useState({});
  const [editMode, setEditMode] = useState({});
  const [editData, setEditData] = useState({});
  const [feedback, setFeedback] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const headers = { 'Content-Type': 'application/json', 'x-admin-secret': secret };

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await fetch(`${API}/admin/analytics`, { headers });
      if (res.ok) setAnalytics(await res.json());
    } finally {
      setAnalyticsLoading(false);
    }
  }, [secret]); // eslint-disable-line react-hooks/exhaustive-deps

  async function uploadImage(file) {
    const form = new FormData();
    form.append('image', file);
    const res = await fetch(`${API}/admin/upload`, {
      method: 'POST',
      headers: { 'x-admin-secret': secret },
      body: form
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.url;
  }

  const loadProducts = useCallback(async () => {
    const res = await fetch(`${API}/admin/shop/products`, { headers });
    if (res.status === 401) { setAuthed(false); return; }
    const data = await res.json();
    setProducts(Array.isArray(data) ? data : []);
  }, [secret]); // eslint-disable-line react-hooks/exhaustive-deps

  async function login() {
    setAuthError('');
    if (!totpCode.trim()) return;
    try {
      const res = await fetch(`${API}/admin/totp/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: totpCode.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('admin_session', data.token);
        setSecret(data.token);
        setAuthed(true);
      } else if (res.status === 401) {
        setAuthError('Ungültiger Code. Bitte erneut versuchen.');
      } else if (res.status === 503) {
        setAuthError('TOTP nicht konfiguriert – TOTP_SECRET fehlt in .env');
      } else {
        setAuthError(`Server-Fehler: ${res.status}`);
      }
    } catch {
      setAuthError('Server nicht erreichbar.');
    }
  }

  async function loadQr() {
    if (!setupSecret.trim()) return;
    try {
      const res = await fetch(`${API}/admin/totp/qr`, {
        headers: { 'x-admin-secret': setupSecret.trim() },
      });
      if (res.ok) {
        const data = await res.json();
        setQrData(data);
      } else {
        setAuthError('Falscher Admin-Secret für Setup.');
      }
    } catch {
      setAuthError('Server nicht erreichbar.');
    }
  }

  useEffect(() => {
    if (authed) loadProducts();
  }, [authed, loadProducts]);

  function flash(msg) {
    setFeedback(msg);
    setTimeout(() => setFeedback(''), 3000);
  }

  async function addProduct() {
    if (!newProduct.name.trim()) return;
    const res = await fetch(`${API}/admin/shop/products`, {
      method: 'POST', headers, body: JSON.stringify(newProduct)
    });
    if (res.ok) {
      setNewProduct({ name: '', description: '', image_url: '' });
      loadProducts();
      flash('Produkt hinzugefügt.');
    }
  }

  async function saveEdit(id) {
    const res = await fetch(`${API}/admin/shop/products/${id}`, {
      method: 'PUT', headers, body: JSON.stringify(editData[id])
    });
    if (res.ok) {
      setEditMode(prev => ({ ...prev, [id]: false }));
      loadProducts();
      flash('Gespeichert.');
    }
  }

  async function deleteProduct(id) {
    if (!window.confirm('Produkt wirklich löschen?')) return;
    await fetch(`${API}/admin/shop/products/${id}`, { method: 'DELETE', headers });
    loadProducts();
    flash('Produkt gelöscht.');
  }

  async function toggleActive(product) {
    await fetch(`${API}/admin/shop/products/${product.id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ ...product, active: !product.active })
    });
    loadProducts();
  }

  async function addProgress(productId) {
    const inp = progressInputs[productId] || {};
    if (!inp.log_text || inp.percent === undefined) return;
    const res = await fetch(`${API}/admin/shop/products/${productId}/progress`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ percent: Number(inp.percent), log_text: inp.log_text, image_url: inp.image_url || null })
    });
    if (res.ok) {
      const data = await res.json();
      setProgressInputs(prev => ({ ...prev, [productId]: {} }));
      loadProducts();
      flash(`Fortschritt gespeichert. ${data.notified} E-Mail(s) versendet.`);
    }
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-sm shadow-2xl">

          {!showSetup ? (
            <>
              <h1 className="text-2xl font-bold mb-1 bg-gradient-to-r from-blue-500 to-purple-600 text-transparent bg-clip-text">
                Admin
              </h1>
              <p className="text-gray-400 text-sm mb-6">6-stelligen Code aus der Authenticator-App eingeben</p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && login()}
                autoFocus
                className="w-full px-4 py-3 rounded-xl bg-gray-700 border border-gray-600 text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-blue-500 mb-3 text-center text-2xl tracking-[0.4em] font-mono"
              />
              {authError && <p className="text-red-400 text-sm mb-3">{authError}</p>}
              <button
                onClick={login}
                className="w-full py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-colors mb-4"
              >
                Einloggen
              </button>
              <button
                onClick={() => { setShowSetup(true); setAuthError(''); }}
                className="w-full text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                Ersteinrichtung / QR-Code anzeigen
              </button>
            </>
          ) : (
            <>
              <h1 className="text-xl font-bold mb-1 text-white">TOTP Einrichten</h1>
              <p className="text-gray-400 text-sm mb-5">Admin-Secret eingeben um den QR-Code zu laden</p>
              <input
                type="password"
                placeholder="Admin-Secret"
                value={setupSecret}
                onChange={e => setSetupSecret(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadQr()}
                autoFocus
                className="w-full px-4 py-3 rounded-xl bg-gray-700 border border-gray-600 text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-blue-500 mb-3"
              />
              {authError && <p className="text-red-400 text-sm mb-3">{authError}</p>}
              <button
                onClick={loadQr}
                className="w-full py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold transition-colors mb-4"
              >
                QR-Code laden
              </button>
              {qrData && (
                <div className="text-center mb-4">
                  <img src={qrData.qr} alt="TOTP QR Code" className="mx-auto rounded-xl mb-3 w-48 h-48" />
                  <p className="text-xs text-gray-400 mb-1">Oder manuell eintragen:</p>
                  <code className="text-xs bg-gray-900 px-3 py-1.5 rounded-lg text-green-400 break-all select-all">
                    {qrData.secret}
                  </code>
                </div>
              )}
              <button
                onClick={() => { setShowSetup(false); setQrData(null); setAuthError(''); }}
                className="w-full text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                ← Zurück zum Login
              </button>
            </>
          )}

        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-transparent bg-clip-text">
            Admin Dashboard
          </h1>
          <button
            onClick={() => { localStorage.removeItem('admin_session'); setSecret(''); setTotpCode(''); setAuthed(false); }}
            className="text-gray-400 hover:text-white text-sm transition-colors"
          >
            Abmelden
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[['shop', 'Shop'], ['analytics', 'Analytics']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => { setActiveTab(key); if (key === 'analytics') loadAnalytics(); }}
              className={`px-5 py-2 rounded-xl text-sm font-semibold transition-colors ${activeTab === key ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Feedback */}
        {feedback && (
          <div className="bg-green-600 text-white px-4 py-3 rounded-xl mb-6 text-sm">
            {feedback}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            {analyticsLoading && <p className="text-gray-400 text-sm mb-4">Lade Daten…</p>}
            {analytics && (
              <>
                {/* Kennzahlen */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {[
                    { label: 'Heute', value: analytics.today },
                    { label: 'Letzte 30 Tage', value: analytics.total },
                    { label: 'Unique IPs (30d)', value: analytics.unique_ips },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-gray-800 rounded-2xl p-4 border border-gray-700 text-center">
                      <div className="text-2xl font-bold text-blue-400">{value}</div>
                      <div className="text-xs text-gray-400 mt-1">{label}</div>
                    </div>
                  ))}
                </div>

                {/* Besuche pro Tag */}
                <div className="bg-gray-800 rounded-2xl p-5 mb-6 border border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-300 mb-4">Besuche letzte 14 Tage</h2>
                  <div className="flex items-end gap-1.5 h-24">
                    {analytics.perDay.map(({ date, visits }) => {
                      const max = Math.max(...analytics.perDay.map(d => d.visits), 1);
                      return (
                        <div key={date} className="flex flex-col items-center flex-1 gap-1" title={`${date}: ${visits}`}>
                          <div
                            className="w-full bg-blue-500 rounded-t opacity-80 hover:opacity-100 transition-opacity"
                            style={{ height: `${Math.max((visits / max) * 80, 3)}px` }}
                          />
                          <span className="text-gray-600 text-[9px] rotate-45 origin-left whitespace-nowrap">
                            {new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                          </span>
                        </div>
                      );
                    })}
                    {analytics.perDay.length === 0 && <p className="text-gray-500 text-sm">Noch keine Daten.</p>}
                  </div>
                </div>

                {/* Top Seiten */}
                <div className="bg-gray-800 rounded-2xl p-5 mb-6 border border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-300 mb-3">Top Seiten (30d)</h2>
                  <div className="space-y-2">
                    {analytics.topPages.map(({ path, visits }) => (
                      <div key={path} className="flex justify-between items-center text-sm">
                        <span className="text-gray-300 font-mono truncate max-w-[75%]">{path}</span>
                        <span className="text-blue-400 font-semibold ml-2">{visits}×</span>
                      </div>
                    ))}
                    {analytics.topPages.length === 0 && <p className="text-gray-500 text-sm">Noch keine Daten.</p>}
                  </div>
                </div>

                {/* Letzte Besuche */}
                <div className="bg-gray-800 rounded-2xl p-5 mb-6 border border-gray-700">
                  <div className="flex justify-between items-center mb-3">
                    <h2 className="text-sm font-semibold text-gray-300">Letzte Besuche</h2>
                    <button
                      onClick={loadAnalytics}
                      className="text-xs text-gray-400 hover:text-white transition-colors"
                    >
                      ↻ Aktualisieren
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead>
                        <tr className="text-gray-500 border-b border-gray-700">
                          <th className="pb-2 pr-4">Zeit</th>
                          <th className="pb-2 pr-4">IP</th>
                          <th className="pb-2 pr-4">Pfad</th>
                          <th className="pb-2">Browser</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.recent.map((v, i) => (
                          <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                            <td className="py-1.5 pr-4 text-gray-400 whitespace-nowrap">
                              {new Date(v.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="py-1.5 pr-4 text-gray-300 font-mono">{v.ip}</td>
                            <td className="py-1.5 pr-4 text-blue-300 font-mono truncate max-w-[150px]">{v.path}</td>
                            <td className="py-1.5 text-gray-400 truncate max-w-[200px]" title={v.user_agent}>
                              {v.user_agent?.split(' ').slice(-1)[0] || '–'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
            {!analytics && !analyticsLoading && (
              <p className="text-gray-500 text-sm">Keine Daten. Backend muss laufen.</p>
            )}
          </div>
        )}

        {/* Shop Tab */}
        {activeTab === 'shop' && <>

        {/* Neues Produkt */}
        <div className="bg-gray-800 rounded-2xl p-5 mb-6 border border-gray-700">
          <h2 className="text-lg font-semibold mb-4 text-gray-200">Neues Produkt</h2>
          <div className="grid gap-3">
            <input
              placeholder="Name *"
              value={newProduct.name}
              onChange={e => setNewProduct(p => ({ ...p, name: e.target.value }))}
              className="px-4 py-2.5 rounded-xl bg-gray-700 border border-gray-600 text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <textarea
              placeholder="Beschreibung"
              value={newProduct.description}
              onChange={e => setNewProduct(p => ({ ...p, description: e.target.value }))}
              rows={2}
              className="px-4 py-2.5 rounded-xl bg-gray-700 border border-gray-600 text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
            />
            <div>
              <label className="text-xs text-gray-400 block mb-1">Bild (optional)</label>
              <input type="file" accept="image/*"
                onChange={async e => {
                  if (!e.target.files[0]) return;
                  const url = await uploadImage(e.target.files[0]);
                  if (url) setNewProduct(p => ({ ...p, image_url: url }));
                }}
                className="text-sm text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:text-xs file:cursor-pointer"
              />
              {newProduct.image_url && <img src={newProduct.image_url} alt="" className="mt-2 h-16 rounded-lg object-cover" />}
            </div>
            <button
              onClick={addProduct}
              className="px-5 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-medium text-sm transition-colors self-start"
            >
              Hinzufügen
            </button>
          </div>
        </div>

        {/* Produkte */}
        <h2 className="text-lg font-semibold mb-4 text-gray-200">Produkte</h2>
        {products.length === 0 && (
          <p className="text-gray-500 text-sm">Noch keine Produkte.</p>
        )}
        <div className="grid gap-4">
          {products.map(p => (
            <div key={p.id} className={`bg-gray-800 rounded-2xl p-5 border ${p.active ? 'border-gray-700' : 'border-gray-800 opacity-60'}`}>

              {/* Produkt-Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="font-semibold text-white">{p.name}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${p.active ? 'bg-green-900 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
                    {p.active ? 'aktiv' : 'inaktiv'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditMode(prev => ({ ...prev, [p.id]: !prev[p.id] }));
                      setEditData(prev => ({ ...prev, [p.id]: { name: p.name, description: p.description || '', image_url: p.image_url || '', active: p.active } }));
                    }}
                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Bearbeiten
                  </button>
                  <button
                    onClick={() => toggleActive(p)}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    {p.active ? 'Deaktivieren' : 'Aktivieren'}
                  </button>
                  <button
                    onClick={() => deleteProduct(p.id)}
                    className="text-xs text-red-500 hover:text-red-400 transition-colors"
                  >
                    Löschen
                  </button>
                </div>
              </div>

              {/* Edit-Form */}
              {editMode[p.id] && (
                <div className="grid gap-2 mb-4 p-3 bg-gray-900 rounded-xl">
                  <input
                    value={editData[p.id]?.name || ''}
                    onChange={e => setEditData(prev => ({ ...prev, [p.id]: { ...prev[p.id], name: e.target.value } }))}
                    className="px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white text-sm outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Name"
                  />
                  <textarea
                    value={editData[p.id]?.description || ''}
                    onChange={e => setEditData(prev => ({ ...prev, [p.id]: { ...prev[p.id], description: e.target.value } }))}
                    rows={2}
                    className="px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white text-sm outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                    placeholder="Beschreibung"
                  />
                  <div>
                    <input type="file" accept="image/*"
                      onChange={async e => {
                        if (!e.target.files[0]) return;
                        const url = await uploadImage(e.target.files[0]);
                        if (url) setEditData(prev => ({ ...prev, [p.id]: { ...prev[p.id], image_url: url } }));
                      }}
                      className="text-xs text-gray-300 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer"
                    />
                    {editData[p.id]?.image_url && <img src={editData[p.id].image_url} alt="" className="mt-1 h-12 rounded-lg object-cover" />}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(p.id)} className="px-4 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm transition-colors">Speichern</button>
                    <button onClick={() => setEditMode(prev => ({ ...prev, [p.id]: false }))} className="px-4 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm transition-colors">Abbrechen</button>
                  </div>
                </div>
              )}

              {/* Fortschritt aktualisieren */}
              <div className="border-t border-gray-700 pt-4">
                <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Fortschritt aktualisieren</p>

                {/* Aktueller Fortschrittsbalken */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-500">Aktuell</span>
                    <span className="text-blue-400 font-medium">{p.progress_percent}%</span>
                  </div>
                  <div className="w-full rounded-full h-1.5 bg-gray-700">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-1.5 rounded-full transition-all"
                      style={{ width: `${p.progress_percent}%` }}
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-400 w-16 shrink-0">Prozent</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      placeholder="0–100"
                      value={progressInputs[p.id]?.percent ?? ''}
                      onChange={e => setProgressInputs(prev => ({ ...prev, [p.id]: { ...prev[p.id], percent: e.target.value } }))}
                      className="flex-1 px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white text-sm outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-start gap-2">
                    <label className="text-xs text-gray-400 w-16 shrink-0 pt-2">Update</label>
                    <textarea
                      placeholder="Was hat sich diese Woche getan?"
                      value={progressInputs[p.id]?.log_text || ''}
                      onChange={e => setProgressInputs(prev => ({ ...prev, [p.id]: { ...prev[p.id], log_text: e.target.value } }))}
                      rows={2}
                      className="flex-1 px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white text-sm outline-none focus:ring-1 focus:ring-blue-500 resize-none placeholder-gray-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-400 w-16 shrink-0">Bild</label>
                    <div className="flex-1">
                      <input type="file" accept="image/*"
                        onChange={async e => {
                          if (!e.target.files[0]) return;
                          const url = await uploadImage(e.target.files[0]);
                          if (url) setProgressInputs(prev => ({ ...prev, [p.id]: { ...prev[p.id], image_url: url } }));
                        }}
                        className="text-xs text-gray-300 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer"
                      />
                      {progressInputs[p.id]?.image_url && <img src={progressInputs[p.id].image_url} alt="" className="mt-1 h-12 rounded-lg object-cover" />}
                    </div>
                  </div>
                  <button
                    onClick={() => addProgress(p.id)}
                    className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors self-start ml-[4.5rem]"
                  >
                    Update senden + E-Mails
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>}

      </div>
    </div>
  );
}
